import { RootStateInterface } from '@/store/rootState';
import { ActionTree, Module, MutationTree } from 'vuex';
import { NoteApi } from '../api/note';
import { GetNotesRequest, Note } from '../models/Note';
import { sortNotesNewestFirst } from '../utils/noteSort';

export interface NotesStateInterface {
  byRequestKey: Record<string, Note[]>;
}

interface FetchNotesPayload {
  request: GetNotesRequest | { entityType: string; entityId: number };
  forceRefresh?: boolean;
}

const inFlightByRequestKey: Record<string, Promise<Note[]> | undefined> = {};

function buildNotesRequestKey(request: { entityType: string; entityId: number }): string {
  return `${request.entityType}:${request.entityId}`;
}

export const state: NotesStateInterface = {
  byRequestKey: {},
};

export const mutations: MutationTree<NotesStateInterface> = {
  setForRequestKey(oldState, payload: { requestKey: string; notes: Note[] }) {
    const sortedNotes = sortNotesNewestFirst(payload.notes);
    oldState.byRequestKey = {
      ...oldState.byRequestKey,
      [payload.requestKey]: sortedNotes,
    };
  },
  invalidateRequestKey(oldState, requestKey: string) {
    const next = { ...oldState.byRequestKey };
    delete next[requestKey];
    oldState.byRequestKey = next;
  },
  reset(oldState) {
    oldState.byRequestKey = {};
  },
};

export const actions: ActionTree<NotesStateInterface, RootStateInterface> = {
  async fetchNotes({ state: st, commit }, payload: FetchNotesPayload) {
    const request = {
      entityType: payload.request.entityType,
      entityId: payload.request.entityId,
    };
    const requestKey = buildNotesRequestKey(request);
    if (!payload.forceRefresh && st.byRequestKey[requestKey]) {
      return sortNotesNewestFirst(st.byRequestKey[requestKey]);
    }
    if (inFlightByRequestKey[requestKey]) {
      return inFlightByRequestKey[requestKey];
    }

    const run = async () => {
      const api = new NoteApi();
      const notes = await api.fetch(new GetNotesRequest(request));
      const sortedNotes = sortNotesNewestFirst(notes);
      commit('setForRequestKey', { requestKey, notes: sortedNotes });
      return sortedNotes;
    };

    inFlightByRequestKey[requestKey] = run().finally(() => {
      delete inFlightByRequestKey[requestKey];
    });

    return inFlightByRequestKey[requestKey];
  },
  invalidateNotesForRequest({ commit }, request: { entityType: string; entityId: number }) {
    commit('invalidateRequestKey', buildNotesRequestKey(request));
  },
};

const namespaced = true;

export const notes: Module<NotesStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  actions,
};
